import * as Yup from 'yup'
import React from 'react'
import { FilePreview, FormItemProps } from '@/components/Form/types'
import ImageComponent from '@/components/UiElements/ImageComponent'
import MaterialPicker from '@/components/ProductProfile/components/MaterialPicker'
import EngravingSetting from '@/components/ProductProfile/components/EngravingSetting'
import { getSectionAssignmentItems } from './sectionAssignmentItems'
import store from '@/store'


// Colour profiles are inkjet: the shopper picks colours per element in the
// customizer and uploads print as-is, so there is no engraving substrate for
// the admin to choose. Unknown defaults to laser — nothing should vanish from
// an older profile that predates the setting.
const isLaserProfile = (profileType?: string) => profileType !== 'color'

interface Section2dFormItemsOptions {
	onDeleteShape: () => void
	profileType?: string
}

export const hasSectionShape = (formData: Record<string, any> = {}) => {
	const shapeType = formData.shapeType?.value || formData.shapeType || 'rectangle'
	const hasDimensions = Number(formData.width) > 0 && Number(formData.height) > 0

	if (shapeType === 'polygon') {
		return Array.isArray(formData.vertices) && formData.vertices.length >= 3
	}

	if (shapeType === 'circle') {
		return Number(formData.radius) > 0 || hasDimensions
	}

	return hasDimensions
}

const createSection2dFormItems = ({ onDeleteShape, profileType }: Section2dFormItemsOptions): Array<FormItemProps> => [

	{
		name: 'sectionId',
		type: 'Hidden',
	},
	{
		name: 'name',
		label: 'Name',
		help: 'Enter a name that identifies this customizable section, such as Front, Back, or Handle.',
		placeholder: 'Enter section name',
		type: 'Text',
		defaultValue: null,
		validation: Yup.string()
			.required('Required')
			.test('name-format', 'Only letters, numbers, and underscores are allowed', function (value) {
				if (store.companySetting?.machine === 'neatolabs') {
					return /^[a-zA-Z0-9_]+$/.test(value || '')
				}
				return true
			}),
		classNames: { container: 'col-span-12' },
		autoFocus: true,
	},
	{
		name: 'sectionImage',
		type: 'File',
		label: 'Section Product Image',
		help: 'Upload the product image used to display and calibrate this section in the 2D preview.',
		classNames: {
			container: 'col-span-12',
			content: '!h-[83px]',
		},
		placeholder: 'Drag and drop your product image',
		itemOptions: {
			filePreview: (file: FilePreview) => (
				file.string ? <ImageComponent
					src={file.string}
					style={{ height: '70px', width: '70px' }}
					className="w-[70px] h-[70px]"
					id={file.uuid}
				/> : null
			),
			acceptFormat: 'image/*, .avif, .webp, .svg, .png, .jpg, .jpeg',
		},
	},
	{
		name: 'shapeType',
		label: 'Shape Type',
		help: 'Choose the boundary shape of the customizable area. Rectangle and circle use dimensions, while polygon uses points drawn on the preview.',
		type: 'Select',
		defaultValue: { value: 'rectangle', label: 'Rectangle' },
		itemOptions: {
			list: [
				{ value: 'rectangle', label: 'Rectangle' },
				{ value: 'circle', label: 'Circle' },
				{ value: 'polygon', label: 'Polygon' },
			],
		},
		classNames: { container: 'col-span-10' },
	},
	{
		name: 'deleteShape',
		type: 'Preview',
		itemOptions: {
			preview: () => (
				<button
					type="button"
					aria-label="Delete shape"
					title="Delete shape"
					className="mt-[28px] flex h-[43px] w-[43px] items-center justify-center rounded-md bg-transparent text-sm text-red-500 transition-all hover:bg-red-500 hover:text-white active:bg-red-600 active:text-white"
					onClick={onDeleteShape}
				>
					<span className="font-icon-regular"></span>
				</button>
			),
		},
		classNames: {
			container: 'col-span-2 !min-h-0 items-end',
			content: 'items-end',
		},
		condition: hasSectionShape,
	},
	{
		name: 'verticesInfo',
		type: 'Preview',
		itemOptions: {
			preview: (props: any) => {
				const vertices = props.formData?.vertices
				const verticesCount = Array.isArray(vertices) ? vertices.length : 0
				const verticesLabel = (
					<div className="FormContainerLabel select-none text-text-md text-gray-900">
						Polygon Vertices <span className="text-gray-500">({verticesCount} points)</span>
					</div>
				)

				if (!vertices || vertices.length === 0) {
					return (
						<div className="flex flex-col gap-sm">
							{verticesLabel}
							<div className="rounded-md border border-gray-300 bg-white p-md">
								<span className="text-text-sm text-gray-400 italic">
									No vertices defined. Draw on the canvas to create polygon shape.
								</span>
							</div>
						</div>
					)
				}

				const inputClass = 'transition-all outline-none w-full text-text-sm text-gray-900 rounded-md border border-gray-300 bg-white placeholder:text-gray-400 focus:shadow-focus focus:border-primary-500 hover:border-primary-500 shadow-xs h-[32px] px-sm'

				const handleVertexChange = (index: number, axis: 'x' | 'y', value: string) => {
					const updated = vertices.map((v: any, i: number) =>
						i === index ? { ...v, [axis]: Number(value) || 0 } : { ...v }
					)
					props.onChange('vertices', updated)
				}

				return (
					<div className="flex flex-col gap-sm">
						{verticesLabel}
						<div className="flex flex-col gap-sm rounded-md border border-gray-300 bg-white p-md">
							{vertices.map((v: any, i: number) => (
								<div key={i} className="flex flex-row items-center gap-xs">
									<span className="text-text-xs text-gray-400 w-[20px] shrink-0">{i + 1}.</span>
									<span className="text-text-xs text-gray-400 shrink-0">X</span>
									<input
										type="number"
										value={Number(v.x).toFixed(1)}
										onChange={(e) => handleVertexChange(i, 'x', e.target.value)}
										className={inputClass}
										step="0.1"
									/>
									<span className="text-text-xs text-gray-400 shrink-0">Y</span>
									<input
										type="number"
										value={Number(v.y).toFixed(1)}
										onChange={(e) => handleVertexChange(i, 'y', e.target.value)}
										className={inputClass}
										step="0.1"
									/>
								</div>
							))}
						</div>
					</div>
				)
			},
		},
		classNames: { container: 'col-span-12' },
		condition: (data: any) => {
			const st = data?.shapeType?.value || data?.shapeType
			return st === 'polygon'
		},
	},
	{
		name: 'offsetX',
		label: 'Offset X (mm)',
		help: 'Set the horizontal position of the section center relative to the center of the calibrated product image.',
		placeholder: '0',
		type: 'Number',
		itemOptions: { min: -10000, max: 10000 },
		defaultValue: null,
		classNames: { container: 'col-span-6' },
	},
	{
		name: 'offsetY',
		label: 'Offset Y (mm)',
		help: 'Set the vertical position of the section center, measured upward from the bottom of the calibrated product image.',
		placeholder: '0',
		type: 'Number',
		itemOptions: { min: -10000, max: 10000 },
		defaultValue: null,
		classNames: { container: 'col-span-6' },
	},
	{
		name: 'width',
		label: 'Width (mm)',
		help: 'Set the physical width of the rectangular customizable area in millimeters.',
		placeholder: '0',
		type: 'Number',
		itemOptions: { min: 0, max: 10000 },
		defaultValue: null,
		classNames: { container: 'col-span-6' },
		condition: (data: any) => {
			const st = data?.shapeType?.value || data?.shapeType
			return st === 'rectangle' || !st
		},
	},
	{
		name: 'height',
		label: 'Height (mm)',
		help: 'Set the physical height of the rectangular customizable area in millimeters.',
		placeholder: '0',
		type: 'Number',
		itemOptions: { min: 0, max: 10000 },
		defaultValue: null,
		classNames: { container: 'col-span-6' },
		condition: (data: any) => {
			const st = data?.shapeType?.value || data?.shapeType
			return st === 'rectangle' || !st
		},
	},
	{
		name: 'radius',
		label: 'Radius (mm)',
		help: 'Set the physical radius of the circular customizable area in millimeters.',
		placeholder: '0',
		type: 'Number',
		itemOptions: { min: 0, max: 10000 },
		defaultValue: null,
		classNames: { container: 'col-span-6' },
		condition: (data: any) => {
			const st = data?.shapeType?.value || data?.shapeType
			return st === 'circle'
		},
	},
	{
		name: 'rotation',
		label: 'Rotation (°)',
		help: 'Rotate the section boundary on the 2D product image. Use a value from -180 to 180 degrees.',
		placeholder: '0',
		type: 'Number',
		itemOptions: { min: -180, max: 180 },
		defaultValue: '0',
		classNames: { container: 'col-span-6' },
	},
	{
		name: 'materialType',
		label: 'Material Type',
		help: 'Choose the material effect used to represent engraved artwork in the 2D preview.',
		placeholder: 'Select material type',
		type: 'Select',
		defaultValue: { value: 'steel', label: 'Steel / Metal' },
		itemOptions: {
			list: [
				{ value: 'custom', label: 'Custom (upload pattern)' },
				{ value: 'steel', label: 'Steel / Metal' },
				{ value: 'gold', label: 'Gold' },
				{ value: 'sandblast', label: 'Sandblast / Frosted' },
				{ value: 'anodized', label: 'Anodized' },
				{ value: 'wood_burn', label: 'Wood Burn' },
				{ value: 'glass_etch', label: 'Glass Etch' },
				{ value: 'color', label: 'Direct Color' },
			],
		},
		classNames: { container: 'col-span-12' },
		condition: () => isLaserProfile(profileType),
	},
	{
		name: 'materialSource',
		label: 'Custom Material',
		help: 'Choose a solid colour or upload a pattern image for the engraved artwork. A custom material uses one or the other, not both.',
		type: 'Preview',
		defaultValue: null,
		itemOptions: {
			preview: (props: any) => (
				<MaterialPicker formData={props?.formData} onChange={props?.onChange} />
			),
		},
		validation: Yup.mixed().test('material-source-required', 'Required', function (value) {
			// Anything that is not a populated object means this section has no custom
			// material to check — a colour profile (where the field is hidden), or a
			// laser section on a built-in material. Note the form coerces a missing
			// value to '' rather than null, so a null check alone would reject those.
			if (!value || typeof value !== 'object') return true
			// Siblings are not visible here (each item validates alone), so this only
			// checks shape. mapSectionToSave enforces that a custom material really
			// has a colour or a pattern, where the whole section is in scope.
			const source = value as { mode?: string; color?: string; file?: unknown }
			return source.mode === 'pattern' ? source.file != null : !!source.color
		}),
		classNames: { container: 'col-span-12' },
		condition: (data: any) => isLaserProfile(profileType)
			&& (data?.materialType?.value || data?.materialType) === 'custom',
	},
	{
		name: 'engravingTint',
		label: 'Engraving Tint',
		help: 'Adjust the brightness of the engraved area in the 2D preview. Lower values appear darker and higher values appear lighter.',
		type: 'Range',
		defaultValue: 0.5,
		itemOptions: { min: 0, step: 0.01, max: 1 },
		classNames: { container: 'col-span-12' },
	},
	{
		name: 'overlayOpacity',
		label: 'Overlay Opacity',
		help: 'Adjust how strongly the section overlay is blended onto the product image in the 2D preview.',
		type: 'Range',
		defaultValue: 1.0,
		itemOptions: { min: 0, step: 0.01, max: 1 },
		classNames: { container: 'col-span-12' },
	},
	{
		name: 'mask',
		type: 'File',
		label: 'Section Mask Image (Optional)',
		hint: 'Usually used to mask out product logo areas or handles.',
		help: 'Upload an optional image mask to exclude parts of the product from the customizable section.',
		classNames: {
			container: 'col-span-12',
		},
		placeholder: 'Drag & Drop Mask image here',
		itemOptions: {
			filePreview: (file: FilePreview) => (
				file.string ? <ImageComponent
					src={file.string}
					style={{ height: '70px', width: '70px' }}
					className="w-[70px] h-[70px]"
					id={file.uuid}
				/> : null
			),
			acceptFormat: 'image/*, .avif, .webp, .svg, .png, .jpg, .jpeg',
		},
	},
	...getSectionAssignmentItems(),

	{
		name: 'engravingSetting',
		label: 'Engraving Setting',
		type: 'Preview',
		itemOptions: {
			preview: (props: any) => (
				<EngravingSetting onChange={props?.onChange} formData={props?.formData} updateForm={props?.updateForm} />
			),
		},
		defaultValue: null,
		validation: Yup.mixed().test('engraving-required', 'Required', function (value) {
			if (store.companySetting?.machine === 'neatolabs') {
				return value != null && typeof value === 'object'
			}
			return true
		}),
		classNames: { container: 'col-span-12' },
		condition: () => store.companySetting?.machine === 'neatolabs',
	},

	// Hidden fields for compatibility with save flow
	{
		name: 'targetObject',
		type: 'Hidden',
	},
	{
		name: 'vertices',
		type: 'Hidden',
	},
	{
		name: 'radialAngle',
		type: 'Hidden',
		defaultValue: '0',
	},
	{
		name: 'centerRadialAngle',
		type: 'Hidden',
		defaultValue: '0',
	},
	{
		name: 'sectionAngle',
		type: 'Hidden',
	},
	{
		name: 'divider',
		type: 'Divider',
		classNames: { container: 'col-span-12' },
	},

]

export default createSection2dFormItems
